import * as React from 'react';
import { SGBase, SGBaseProps, OptionalVector2, FillOptionalVector2, Vector2ToString, Vector2ToArray } from './sgbase';
import { SGApp } from './sgapp';
import { Vector2 } from '../utils/transform';
import bind from 'bind-decorator';
import { SgNode, CreateSgNode, GetOwningOverlayKey, GetOwningOverlayHandle, FullyQualifyIDIfNeeded, TraversalState, BuildSgNodeReturn } from './sgproto';

export interface UVs
{
	u: number;
	v: number;
}

export function UVsToArray( uv: UVs ): [number, number]
{
	if ( !uv )
	{
		return undefined;
	}

	return [uv.u, uv.v];
}

export enum EPanelOrigin
{
	TopLeft,
	TopCenter,
	TopRight,
	CenterLeft,
	Center,
	CenterRight,
	BottomLeft,
	BottomCenter,
	BottomRight,
}

export function PanelOriginToVector2( eOrigin: EPanelOrigin ): Vector2
{
	switch ( eOrigin )
	{
		case EPanelOrigin.TopLeft:
			return { x: -1, y: 1 };
		case EPanelOrigin.TopCenter:
			return { x: 0, y: 1 };
		case EPanelOrigin.TopRight:
			return { x: 1, y: 1 };
		case EPanelOrigin.CenterLeft:
			return { x: -1, y: 0 };
		case EPanelOrigin.Center:
			return { x: 0, y: 0 };
		case EPanelOrigin.CenterRight:
			return { x: 1, y: 0 };
		case EPanelOrigin.BottomLeft:
			return { x: -1, y: -1 };
		case EPanelOrigin.BottomCenter:
			return { x: 0, y: -1 };
		case EPanelOrigin.BottomRight:
			return { x: 1, y: -1 };
	}
}

export enum EPanelSampler
{
	Auto = 0,
	SingleTap = 1,
}

export enum EPanelStereoscopy
{
	Mono = 0,				// Texture is not stereo
	Parallel = 1,			// Texture is left/right
	Crossed = 2,			// Texture is crossed and right/left
	Panorama = 3,			// Texture is a panorama
	StackedPanorama = 4,	// Texture is a stereo panorama
}

export enum PanelVisibility
{
	Visible,
	SkipInSceneGraph, // Rendered on page, skipped in scene graph. Prevents page repaints on frequently toggled elements.
	Hidden, // Skipped in scene graph and not rendered on page.
}

export interface PanelProps extends SGBaseProps
{
	overlay_key?: string;
	uv_min?: UVs;
	uv_max?: UVs;
	width?: number;
	height?: number;
	target_dpi_panel_id?: string; // ID of another panel in this scene graph to match the DPI of.
	target_dpi_multiplier?: number;
	curvature?: number;
	curvature_origin_id?: string;
	interactive?: boolean;
	scrollable?: boolean;
	modal?: boolean;
	origin?: EPanelOrigin | OptionalVector2; // Origin of this panel relative to its center (0,0). Range: [-1,1] on x and y.
	debug_name?: string;
	sampler?: EPanelSampler;
	reflect?: number;
	stereoscopy?: EPanelStereoscopy;
	rendermodel_component_device_index?: number;
	rendermodel_component_name?: string;
	texture_id?: string;
	sort_order?: number;
	sort_depth_bias?: number;
	visibility?: PanelVisibility; // Default: Visible
}

interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export class Panel extends SGBase<PanelProps, null>
{
	public static s_bPanelsAreDirty: boolean = false;

	public m_Rect: Rect = { x: 0, y: 0, width: 0, height: 0 };
	public m_nEmbeddedIndex: number = null;

	private m_resizeObserver: any = null;
	private m_UVsMin: UVs = undefined;
	private m_UVsMax: UVs = undefined;
	private m_bOverdragBlocking: boolean = false;
	private m_overdragBlockingElements: HTMLDivElement[] = [];

	constructor( props: any )
	{
		super( props );

		const bIsRenderModelPanel = ( this.props.rendermodel_component_device_index !== undefined || this.props.rendermodel_component_name !== undefined );
		if ( bIsRenderModelPanel && ( this.props.rendermodel_component_device_index === undefined || this.props.rendermodel_component_name === undefined ) )
		{
			throw new Error( "Panel requires both rendermodel_component_device_index and rendermodel_component_name to be a rendermodel texture." );
		}

		if ( this.props.width === undefined && this.props.height === undefined && this.props.target_dpi_panel_id === undefined && !bIsRenderModelPanel )
		{
			throw new Error( "Panel requires either an explicit width, an explicit height, both an explicit width and height, a target_panel_dpi, or be a rendermodel panel." );
		}

		if ( ( this.props.width !== undefined || this.props.height !== undefined ) && this.props.target_dpi_panel_id !== undefined )
		{
			throw new Error( "Panel cannot have both an explicit width/height and target_panel_dpi." );
		}

		super.setBuildNodeOverride( this.buildNode );
	}

	public isExternal(): boolean
	{
		return !!this.props.overlay_key;
	}

	public getEmbeddedIndex(): number
	{
		return this.m_nEmbeddedIndex;
	}

	public componentWillReceiveProps_UNSAFE()
	{
		Panel.s_bPanelsAreDirty = true;
	}

	public componentDidMount()
	{
		super.componentDidMount();


		this.m_resizeObserver = new ResizeObserver( this.onResizeObserved );
		this.m_resizeObserver.observe( this.getCurrentRootElement() );

		this.m_nEmbeddedIndex = SGApp.Current().addEmbeddedPanelUVs( this );
		Panel.s_bPanelsAreDirty = true;

		this.getCurrentRootElement().addEventListener( "mousedown", this.onPanelMouseDown );

		// we need one more update to get the embedded index into the render function
		this.forceUpdate();
	}

	@bind private onResizeObserved( entries: ResizeObserverEntry[], observer: ResizeObserver )
	{
		SGApp.Current().forceLayoutUpdate();
	}

	public componentWillUnmount()
	{
		if ( this.m_resizeObserver )
		{
			this.m_resizeObserver.disconnect();
			this.m_resizeObserver = null;
		}

		this.stopOverDragBlocking();

		this.getCurrentRootElement().removeEventListener( "mousedown", this.onPanelMouseDown );

		Panel.s_bPanelsAreDirty = true;
		SGApp.Current().removeEmbeddedPanelUVs( this );

		super.componentWillUnmount();
	}

	@bind private onPanelMouseDown()
	{
		this.startOverDragBlocking();
	}

	private startOverDragBlocking()
	{
		if ( this.m_bOverdragBlocking )
		{
			return;
		}

		const documentRect = document.body.getBoundingClientRect();
		const panelRect = this.getCurrentRootElement().getBoundingClientRect();

		// Above panel
		this.createOverdragBlockingElement(
			0, 0, documentRect.width, panelRect.y );

		// Below panel
		this.createOverdragBlockingElement(
			0, panelRect.y + panelRect.height, documentRect.width, documentRect.height - panelRect.height - panelRect.y );

		// Left of panel
		this.createOverdragBlockingElement(
			0, panelRect.y, panelRect.x, panelRect.height );

		// Left of panel
		this.createOverdragBlockingElement(
			panelRect.x + panelRect.width, panelRect.y, documentRect.width - panelRect.width - panelRect.x, panelRect.height );

		window.document.addEventListener( "mouseup", this.onWindowMouseUp );

		this.m_bOverdragBlocking = true;
	}

	private stopOverDragBlocking()
	{
		if ( !this.m_bOverdragBlocking )
		{
			return;
		}

		this.m_overdragBlockingElements.forEach( ( elem ) =>
		{
			document.body.removeChild( elem );
		} );
		this.m_overdragBlockingElements = [];

		window.document.removeEventListener( "mouseup", this.onWindowMouseUp );

		this.m_bOverdragBlocking = false;
	}

	private createOverdragBlockingElement( x: number, y: number, width: number, height: number )
	{
		let elem = document.createElement( "div" );

		elem.style.position = "absolute";
		elem.style.top = y + "px";
		elem.style.left = x + "px";
		elem.style.width = width + "px";
		elem.style.height = height + "px";
		elem.style.zIndex = "90019001";

		this.m_overdragBlockingElements.push( elem );
		document.body.appendChild( elem );
	}

	@bind private onWindowMouseUp( event: MouseEvent )
	{
		this.stopOverDragBlocking();
	}

	public getNodeType(): string
	{
		return "panel";
	}

	private get visibility()
	{
		return this.props.visibility ?? PanelVisibility.Visible;
	}

	@bind private buildNode( traversalState: TraversalState, domNode: HTMLElement ): BuildSgNodeReturn
	{
		if ( this.visibility != PanelVisibility.Visible )
		{
			return [ traversalState, null ];
		}

		let mutatedState: TraversalState = { ...traversalState };
		let node = this.createSgNode( domNode );

		let vFilledOrigin: Vector2 = { x: 0, y: 0 };
		if ( typeof this.props.origin === "object" )
		{
			vFilledOrigin = FillOptionalVector2( this.props.origin, { x: 0, y: 0 } );
		}
		else
		{
			vFilledOrigin = PanelOriginToVector2( this.props.origin );
		}

		const sKey = this.props.overlay_key;
		const sOwningOverlayKey = GetOwningOverlayKey();
		if ( sKey && sKey.length > 0 )
		{
			node.properties.key = sKey;
		}
		else if ( sOwningOverlayKey )
		{
			node.properties.key = sOwningOverlayKey;
		}
		else
		{
			node.properties.overlay_handle = GetOwningOverlayHandle();
		}

		node.properties.uv_min = UVsToArray( this.m_UVsMin ) ?? undefined;
		node.properties.uv_max = UVsToArray( this.m_UVsMax ) ?? undefined;

		node.properties.width = this.props.width ?? undefined;
		node.properties.height = this.props.height ?? undefined;
		node.properties["target-dpi-panel-id"] = FullyQualifyIDIfNeeded( this.props.target_dpi_panel_id );
		node.properties["target-dpi-multiplier"] = this.props.target_dpi_multiplier;

		node.properties.curvature = this.props.curvature;
		node.properties["curvature-origin-id"] = FullyQualifyIDIfNeeded( this.props.curvature_origin_id );
		node.properties.interactive = this.props.interactive;
		node.properties.scrollable = this.props.scrollable;
		node.properties.modal = this.props.modal;
		node.properties["embedded-uv-index"] = this.m_nEmbeddedIndex;
		node.properties.origin = Vector2ToArray( vFilledOrigin );
		node.properties.debug_name = this.props.debug_name;
		node.properties.sampler = this.props.sampler;
		node.properties.reflect = this.props.reflect;
		node.properties.stereoscopy = this.props.stereoscopy;
		node.properties.rendermodel_component_device_index = this.props.rendermodel_component_device_index;
		node.properties.rendermodel_component_name = this.props.rendermodel_component_name;
		node.properties["texture-id"] = FullyQualifyIDIfNeeded( this.props.texture_id );
		node.properties["sort-order"] = this.props.sort_order;
		node.properties["sort-depth-bias"] = this.props.sort_depth_bias;

		mutatedState.currentPanel = this;

		return [mutatedState, node];
	}

	public scaleLocalUVToGlobal( localUV: UVs ): UVs
	{
		const uLength = ( this.m_UVsMax.u - this.m_UVsMin.u );
		const vLength = ( this.m_UVsMax.v - this.m_UVsMin.v );

		return {
			u: this.m_UVsMin.u + ( uLength * localUV.u ),
			v: this.m_UVsMin.v + ( vLength * localUV.v ),
		};
	}

	public updateLayoutValues()
	{
		if ( this.props.overlay_key )
		{
			this.m_UVsMin = this.props.uv_min;
			this.m_UVsMax = this.props.uv_max;
			return;
		}

		this.m_Rect = this.getCurrentRootElement().getBoundingClientRect() as DOMRect;

		let actualWindow = this.getCurrentRootElement().ownerDocument.defaultView;

		this.m_UVsMin = {
			u: this.m_Rect.x / actualWindow.innerWidth,
			v: this.m_Rect.y / actualWindow.innerHeight,
		};
		this.m_UVsMax = {
			u: ( this.m_Rect.x + this.m_Rect.width ) / actualWindow.innerWidth,
			v: ( this.m_Rect.y + this.m_Rect.height ) / actualWindow.innerHeight,
		};
	}

	public internalRender(): JSX.Element
	{
		return <vsg-node style={ { display: this.visibility == PanelVisibility.Hidden ? "none" : null } }>
			{ this.props.children }
		</vsg-node>;
	}
}
